/**
 * Backups — List all backup requests across all machines
 * Clean, minimal design
 */
import { T } from '../styles/tokens';
import { useState, useEffect } from 'preact/hooks';
import { Spinner, SkeletonBar } from '../components/ui';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';

interface BackupRequest {
  id: string;
  machine_id?: string;
  runner_id?: string;
  backup_type: string;
  status: string;
  email?: string;
  download_url?: string;
  file_size?: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending:    { label: 'Pendente',     color: T.warning, bg: T.warningBg },
  processing: { label: 'Processando', color: T.primary, bg: T.primaryBg },
  completed:  { label: 'Concluído',   color: T.success, bg: T.successBg },
  failed:     { label: 'Falhou',      color: T.danger, bg: T.dangerBg },
};

function DownloadIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
}

function BrainIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2a3.5 3.5 0 0 0-3.4 2.7A3.5 3.5 0 0 0 3 8.2a3.5 3.5 0 0 0 .7 4.3A3.5 3.5 0 0 0 5 16a3.5 3.5 0 0 0 3.1 2.9A3.5 3.5 0 0 0 12 21"/>
    <path d="M14.5 2a3.5 3.5 0 0 1 3.4 2.7A3.5 3.5 0 0 1 21 8.2a3.5 3.5 0 0 1-.7 4.3 3.5 3.5 0 0 1-1.3 3.5A3.5 3.5 0 0 1 15.9 19 3.5 3.5 0 0 1 12 21"/>
    <path d="M12 2v19"/>
  </svg>;
}

function FolderIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>;
}

function EmptyState() {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{
        width: 64, height: 64, borderRadius: 20, margin: '0 auto 20px',
        background: T.bgCard, border: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <DownloadIcon size={28} />
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: T.text }}>Nenhum backup solicitado</h3>
      <p style={{ margin: 0, fontSize: 13, color: T.textMuted, maxWidth: 360, marginInline: 'auto', lineHeight: 1.6 }}>
        Solicite backups pelo popup do servidor na página de Agentes. Todos os pedidos aparecerão aqui.
      </p>
    </div>
  );
}

export function BackupsPage() {
  if (!isLoggedIn.value) return <LoginPage />;

  const [requests, setRequests] = useState<BackupRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('all');
  const [isPremium, setIsPremium] = useState(false);
  const [autoConfigs, setAutoConfigs] = useState<any[]>([]);

  const fetchRequests = async () => {
    try {
      const [reqRes, subRes, autoRes] = await Promise.all([
        api.get<{ requests: BackupRequest[] }>('/cloud/machines/backup-requests'),
        api.get<{ premium: boolean }>('/subscription/me').catch(() => ({ premium: false })),
        api.get<{ configs: any[] }>('/cloud/auto-backups').catch(() => ({ configs: [] })),
      ]);
      setRequests(reqRes.requests || []);
      setIsPremium(subRes.premium);
      setAutoConfigs(autoRes.configs || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
    const iv = setInterval(fetchRequests, 15000);
    return () => clearInterval(iv);
  }, []);

  const filtered = filter === 'all' ? requests : requests.filter(r => {
    if (filter === 'pending') return r.status === 'pending' || r.status === 'processing';
    return r.status === filter;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending' || r.status === 'processing').length,
    completed: requests.filter(r => r.status === 'completed').length,
    failed: requests.filter(r => r.status === 'failed').length,
  };

  if (loading) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
              <SkeletonBar width="60%" height={16} />
              <div style={{ height: 8 }} />
              <SkeletonBar width="80%" height={12} />
              <div style={{ height: 6 }} />
              <SkeletonBar width="40%" height={12} />
            </div>
          ))}
        </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 840, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: T.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Backups</h1>
        <p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>
          {stats.total} pedido{stats.total !== 1 ? 's' : ''} total
          {stats.pending > 0 && <span style={{ color: T.warning }}> · {stats.pending} pendente{stats.pending !== 1 ? 's' : ''}</span>}
        </p>
      </div>

      {/* Premium banner */}
      {!isPremium && (
        <div onClick={() => window.location.href = '/upgrade'} style={{
          padding: '20px 24px', borderRadius: 14, marginBottom: 20, cursor: 'pointer',
          background: `linear-gradient(135deg, ${T.primaryBgSubtle}, ${T.purpleBgSubtle})`,
          border: `1px solid ${T.primaryBg}`, display: 'flex', alignItems: 'center', gap: 16,
          transition: 'all 0.15s',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: T.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.primary,
          }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Ative o Backup Automático</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Plano Premium a partir de $2.92/mês — proteja todos os seus agentes</div>
          </div>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B7A90" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
      )}

      {/* Auto-backup configs */}
      {isPremium && autoConfigs.length > 0 && (
        <div style={{
          padding: '16px 20px', borderRadius: 14, marginBottom: 20,
          background: T.bgCard, border: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>
            Backup Automático Ativo
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {autoConfigs.filter(c => c.enabled).map((cfg: any) => (
              <div key={cfg.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                borderRadius: 8, background: T.successBg, border: `1px solid ${T.successBg}`,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: 3, background: T.success, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: T.textSecondary, flex: 1 }}>
                  Máquina {cfg.machine_id?.slice(0, 8)}... — {cfg.backup_type === 'full' ? 'Completo' : 'Cérebro'} — Diário
                </span>
                {cfg.last_backup_at && (
                  <span style={{ fontSize: 10, color: T.textMuted }}>
                    Último: {new Date(cfg.last_backup_at).toLocaleDateString('pt-BR')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats cards */}
      {requests.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { key: 'all' as const, label: 'Total', value: stats.total, color: T.textSecondary },
            { key: 'pending' as const, label: 'Pendentes', value: stats.pending, color: T.warning },
            { key: 'completed' as const, label: 'Concluídos', value: stats.completed, color: T.success },
            { key: 'failed' as const, label: 'Falharam', value: stats.failed, color: T.danger },
          ].map(s => (
            <div key={s.key} onClick={() => setFilter(s.key)} style={{
              padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
              background: filter === s.key ? T.primaryBg : T.bgCard,
              border: filter === s.key ? `1px solid ${T.primaryBgStrong}` : `1px solid ${T.border}`,
              transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(req => {
            const st = STATUS_MAP[req.status] || { label: req.status, color: T.textMuted, bg: T.bgCard };
            const isBrain = req.backup_type === 'brain';
            return (
              <div key={req.id} style={{
                padding: '16px 20px', borderRadius: 12,
                background: T.bgCard, border: `1px solid ${T.border}`,
                transition: 'all 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Type icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: T.bgCard, border: `1px solid ${T.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#8B95A5',
                  }}>
                    {isBrain ? <BrainIcon size={18} /> : <FolderIcon size={18} />}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                        {isBrain ? 'Apenas Cérebro' : 'Cérebro + Arquivos'}
                      </span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                        background: st.bg, color: st.color, border: `1px solid ${st.color}30`,
                      }}>{st.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span>
                        {new Date(req.created_at).toLocaleDateString('pt-BR')} {new Date(req.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {req.runner_id && <span>Runner: {req.runner_id.slice(0, 8)}...</span>}
                      {req.file_size && <span>{(req.file_size / 1024 / 1024).toFixed(1)} MB</span>}
                    </div>
                    {req.error_message && (
                      <div style={{ fontSize: 11, color: T.danger, marginTop: 6, padding: '4px 8px', borderRadius: 6, background: T.dangerBg }}>
                        {req.error_message}
                      </div>
                    )}
                  </div>

                  {/* Download button */}
                  {req.status === 'completed' && req.download_url && (
                    <a href={req.download_url} target="_blank" rel="noopener" style={{
                      padding: '8px 14px', borderRadius: 8, textDecoration: 'none',
                      background: T.successBg, border: `1px solid ${T.successBgStrong}`,
                      color: T.success, fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                    }}>
                      <DownloadIcon size={13} /> Baixar
                    </a>
                  )}

                  {/* Processing spinner */}
                  {req.status === 'processing' && (
                    <Spinner size="sm" inline />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* spin removido — usa <Spinner /> */}
    </div>
  );
}
